import { useCallback, useEffect, useState } from 'react';
import { identityApi, staffApi, IdentityError, getIdentitySession, setIdentitySession, type StaffUser, type StaffAuditRow } from '../../lib/identityApi';

// 员工账号管理（Integral 统一身份）。
// 首版严格模式：只有被显式授予 staff.manage 的账号才能进入本页；
// 未获授权时后端返回 staff_manage_required，这里只做提示，不做前端兜底放行。
//
// 与 /portal-admin 的分工：那一页管客户（portal_manage），本页管员工（staff_manage）。
// 两个授权位互相独立，持有其一时在本页不会获得另一份权限。
//
// 复用统一身份会话（x-integral-session），与 core-api 的 Bearer 会话彼此独立，
// 因此本页自带登录入口。

const ROLE_TEXT: Record<string, string> = {
  company_admin: '公司管理员',
  engineer: '工程师（销售/生产/QC/仓库）',
  finance: '财务',
};

const ERROR_TEXT: Record<string, string> = {
  invalid_credentials: '用户名或密码错误，或账号尚未完成密码设置',
  account_locked: '登录失败次数过多，请稍后再试',
  staff_manage_required: '未获授权：本页面需要管理员显式授予 staff.manage 权限',
  session_required: '会话已失效，请重新登录',
  session_invalid: '会话无效，请重新登录',
  session_expired: '会话已过期，请重新登录',
  account_disabled: '账号已被禁用',
  username_taken: '该登录名已被占用',
  email_taken: '该邮箱已被占用',
  invalid_role: '角色不合法',
  username_required: '必须填写登录名',
  // 防自锁的三条：后端拒绝，前端只负责把原因说清楚。
  cannot_disable_self: '不能停用自己 —— 否则会把最后一个能管账号的人关在门外',
  cannot_change_own_role: '不能修改自己的角色',
  cannot_revoke_own_staff_manage: '不能撤销自己的员工管理权',
  not_staff_account: '该账号是客户账号，不在员工管理范围内',
};

const AUDIT_TEXT: Record<string, string> = {
  staff_created: '创建员工',
  staff_invited: '重新邀请',
  staff_updated: '修改资料',
  staff_role_changed: '变更角色',
  staff_manage_granted: '授予员工管理权',
  staff_manage_revoked: '撤销员工管理权',
  factura_manage_granted: '授予 Factura 使用权',
  factura_manage_revoked: '撤销 Factura 使用权',
  staff_disabled: '停用',
  staff_enabled: '启用',
};

// 会话类错误码：出现即视为登录态已失效，退回登录页。
const SESSION_CODES = new Set(['session_required', 'session_invalid', 'session_expired', 'account_disabled']);

function errText(e: unknown): string {
  if (e instanceof IdentityError) return ERROR_TEXT[e.message] || e.message;
  return '操作失败';
}

function setLinkFor(token: string): string {
  return `${window.location.origin}/set-password?token=${encodeURIComponent(token)}`;
}

export function UsersPage() {
  const [session, setSession] = useState<string>(getIdentitySession());
  const [creds, setCreds] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [audit, setAudit] = useState<StaffAuditRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState<string | null>(null);
  const [setLink, setSetLink] = useState<string | null>(null);
  const [form, setForm] = useState({ username: '', email: '', display_name: '', role: 'engineer' });

  const doLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoggingIn(true); setLoginError(null);
    try {
      const r = await identityApi.login(creds.username, creds.password);
      setIdentitySession(r.sessionToken);
      setSession(r.sessionToken);
      setCreds({ username: '', password: '' });
    } catch (err) {
      setLoginError(errText(err));
    } finally {
      setLoggingIn(false);
    }
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      const [u, r, a] = await Promise.all([staffApi.users(), staffApi.roles(), staffApi.audit()]);
      setUsers(u); setRoles(r); setAudit(a);
    } catch (err) {
      // 必须判断**原始错误码**，不能判断翻译后的中文文案：
      // errText() 返回的是给人看的中文，里面根本没有 'staff_manage_required' 这个串，
      // 拿它做 includes 判断永远不会命中（这个坑真实发生过：门禁生效了但页面没提示）。
      const code = err instanceof IdentityError ? err.message : '';
      const msg = errText(err);
      if (code === 'staff_manage_required') setDenied(msg);
      else if (SESSION_CODES.has(code)) { setIdentitySession(''); setSession(''); }
      setError(msg);
    }
  }, []);

  useEffect(() => { if (session) void load(); }, [session, load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };

  const create = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const r = await staffApi.create(form);
      // 邀请链接只在这里出现一次：服务端保存的是摘要，刷新页面就拿不到了。
      setSetLink(setLinkFor(r.token));
      setForm({ username: '', email: '', display_name: '', role: 'engineer' });
      await load();
    });
  };

  const invite = (u: StaffUser) => run(async () => {
    const r = await staffApi.invite(u.userId);
    setSetLink(r.token ? setLinkFor(r.token) : null);
    await load();
  });

  const setActive = (u: StaffUser, active: boolean) => run(async () => {
    if (active) await staffApi.enable(u.userId);
    else await staffApi.disable(u.userId);
    await load();
  });

  const setRole = (u: StaffUser, role: string) => run(async () => {
    await staffApi.update(u.userId, { role });
    await load();
  });

  const setManage = (u: StaffUser, on: boolean) => run(async () => {
    await staffApi.update(u.userId, { staff_manage: on });
    await load();
  });

  const setFactura = (u: StaffUser, on: boolean) => run(async () => {
    await staffApi.update(u.userId, { factura_manage: on });
    await load();
  });

  // 已登录但未获授权：必须**明确停在无权限页**，而不是渲染出完整的管理界面。
  // 否则未授权者仍会看到列表区与「新建员工」表单（虽然每个请求都会被后端拒绝），
  // 这既误导使用者，也让人误以为门禁在前端生效了。
  if (denied) {
    return (
      <div className="login-wrap">
        <div className="login-card">
          <h1>员工账号管理</h1>
          <p className="login-error" data-testid="staff-denied">{denied}</p>
          <button onClick={() => { setIdentitySession(''); setSession(''); setDenied(null); }}>
            换一个账号登录
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="login-wrap">
        <form className="login-card" onSubmit={doLogin}>
          <h1>员工账号管理</h1>
          <p className="login-hint">使用统一身份登录</p>
          {denied && <p className="login-error" data-testid="staff-denied">{denied}</p>}
          <input value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} placeholder="登录名" autoComplete="username" />
          <input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="密码" autoComplete="current-password" />
          {loginError && <p className="login-error">{loginError}</p>}
          <button type="submit" disabled={loggingIn}>{loggingIn ? '登录中…' : '登录'}</button>
        </form>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1>员工账号管理</h1>
        <button onClick={() => void run(async () => { await identityApi.logout(); setIdentitySession(''); setSession(''); })}>
          退出
        </button>
      </header>

      {error && <p className="error-banner" data-testid="staff-error">{error}</p>}

      {setLink && (
        <section className="card" data-testid="staff-set-link">
          <h2>一次性设置密码链接</h2>
          <p className="hint">
            明文只在生成时出现这一次，服务端只保存摘要。请立即复制发给本人；
            链接默认 15 分钟有效，且用后即失效。
          </p>
          <input readOnly value={setLink} data-testid="set-password-link" onFocus={(e) => e.currentTarget.select()} />
          <button onClick={() => setSetLink(null)}>我已复制，关闭</button>
        </section>
      )}

      <section className="card">
        <h2>新建员工并邀请</h2>
        <form onSubmit={create}>
          <input value={form.username} placeholder="登录名" required
            onChange={(e) => setForm({ ...form, username: e.target.value })} />
          {/* 邮箱可选：交付方式是复制邀请链接交给本人，邮件自动化以后再做 */}
          <input value={form.email} type="email" placeholder="邮箱（可选，便于将来自动发送）"
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input value={form.display_name} placeholder="显示名"
            onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {roles.map((r) => <option key={r} value={r}>{ROLE_TEXT[r] || r}</option>)}
          </select>
          <button type="submit" disabled={busy}>创建并生成邀请链接</button>
        </form>
        <p className="hint">
          创建后账号处于「必须设置密码」状态，本人设密前无法登录。系统不会下发临时密码。
          请把下方一次性链接复制发给本人；填了邮箱只是为将来自动发送做准备。
        </p>
      </section>

      <section className="card">
        <h2>员工列表（{users.length}）</h2>
        <table>
          <thead>
            <tr>
              <th>登录名</th><th>显示名</th><th>邮箱</th><th>角色</th>
              <th>状态</th><th>员工管理权</th><th>Factura 使用权</th><th>最近登录</th><th>操作</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.userId} data-testid={`staff-row-${u.username}`}>
                <td>{u.username}</td>
                <td>{u.displayName || '—'}</td>
                <td>{u.email || '—'}</td>
                <td>
                  <select value={u.role} disabled={busy} onChange={(e) => void setRole(u, e.target.value)}>
                    {roles.map((r) => <option key={r} value={r}>{ROLE_TEXT[r] || r}</option>)}
                  </select>
                </td>
                <td>
                  {u.isActive ? '启用' : '已停用'}
                  {u.mustSetPassword && u.isActive ? '（待设密，尚不能登录）' : ''}
                </td>
                <td>
                  <label>
                    <input type="checkbox" checked={u.staffManage} disabled={busy}
                      onChange={(e) => void setManage(u, e.target.checked)} />
                    可管员工
                  </label>
                </td>
                <td>
                  {u.role === 'company_admin' || u.role === 'finance' ? '角色已包含' : (
                    <label>
                      <input type="checkbox" checked={!!u.facturaManage} disabled={busy}
                        onChange={(e) => void setFactura(u, e.target.checked)} />
                      可用 Factura
                    </label>
                  )}
                </td>
                <td>{u.lastLoginAt || '从未登录'}</td>
                <td>
                  <button disabled={busy} onClick={() => void invite(u)}>重新邀请</button>
                  <button disabled={busy} onClick={() => void setActive(u, !u.isActive)}>
                    {u.isActive ? '停用' : '启用'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="hint">
          停用会立即撤销该员工的全部登录会话与未用邀请链接，无需等待会话自然过期。
          改角色同样会立即撤销会话，避免旧角色权限被残留会话继续使用。
        </p>
      </section>

      <section className="card">
        <h2>员工管理操作审计</h2>
        <table>
          <thead>
            <tr><th>时间</th><th>操作</th><th>目标账号</th><th>详情</th></tr>
          </thead>
          <tbody>
            {audit.map((a) => (
              <tr key={a.id}>
                <td>{a.created_at}</td>
                <td>{AUDIT_TEXT[a.action] || a.action}</td>
                <td>{users.find((u) => u.userId === a.target_user_id)?.username || a.target_user_id || '—'}</td>
                <td>{a.detail || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="hint">审计只记操作与字段变更，不含口令，也不含邀请链接明文。</p>
      </section>
    </div>
  );
}
